/**
 * TIER 2 — SEMÁNTICOS
 * ---------------------------------------------------------------------------
 * Cada token responde a "para qué sirve", no a "de qué color es". Es lo que
 * consumen los componentes. Existen dos resoluciones: `light` y `dark`.
 *
 * Regla de oro: si un componente necesita un color y no hay token semántico
 * que lo describa, se crea el token — nunca se usa un primitivo suelto.
 */

import { amber, blue, green, neutral, red, teal, violet, pink } from './primitives';

export type ColorScheme = 'light' | 'dark';

export interface SemanticColors {
  // Superficies
  /** Fondo de la ventana / pantalla. */
  canvas: string;
  /** Fondo de tarjetas, paneles y hojas. */
  surface: string;
  /** Superficie que flota sobre `surface` (popover, menú, modal). */
  surfaceRaised: string;
  /** Superficie hundida (celdas de código, wells, chat entrante). */
  surfaceSunken: string;
  /** Hover de una fila/celda/ítem sobre `surface`. */
  surfaceHover: string;
  /** Estado presionado / seleccionado suave. */
  surfaceActive: string;
  /** Fondo de cabeceras de tabla y barras de herramientas. */
  surfaceHeader: string;
  /** Velo detrás de modales y drawers. */
  overlay: string;

  // Bordes
  /** Divisor decorativo: tablas, tarjetas, separadores. Sin mínimo WCAG. */
  border: string;
  borderStrong: string;
  borderSubtle: string;
  /**
   * Contorno de un control interactivo (input, select, checkbox). A diferencia
   * de `border`, este SÍ identifica un componente de interfaz y por tanto debe
   * alcanzar 3:1 contra la superficie (WCAG 1.4.11).
   */
  borderInput: string;
  /** Borde de un input enfocado. */
  borderFocus: string;

  // Texto e iconos
  fg: string;
  fgMuted: string;
  fgSubtle: string;
  fgDisabled: string;
  /** Texto sobre superficies de marca saturadas. */
  fgOnAccent: string;
  /** Enlaces. */
  link: string;
  linkHover: string;

  // Marca / acción primaria
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryFg: string;
  primarySubtle: string;
  primarySubtleFg: string;
  primaryBorder: string;

  // Acento (cosecha / ámbar) — realces, no acciones destructivas
  accent: string;
  accentHover: string;
  accentFg: string;
  accentSubtle: string;
  accentSubtleFg: string;
  accentBorder: string;

  // Estados
  success: string;
  successFg: string;
  successSubtle: string;
  successSubtleFg: string;
  successBorder: string;

  warning: string;
  warningFg: string;
  warningSubtle: string;
  warningSubtleFg: string;
  warningBorder: string;

  danger: string;
  dangerHover: string;
  dangerFg: string;
  dangerSubtle: string;
  dangerSubtleFg: string;
  dangerBorder: string;

  info: string;
  infoFg: string;
  infoSubtle: string;
  infoSubtleFg: string;
  infoBorder: string;

  // Utilidades
  focusRing: string;
  /** Barra de progreso / pista de slider. */
  track: string;
  /** Separador de secciones. */
  divider: string;
  /** Fondo de un skeleton de carga. */
  skeleton: string;
  /** Resaltado de coincidencia de búsqueda. */
  highlight: string;
  highlightFg: string;
}

export const lightColors: SemanticColors = {
  /**
   * El lienzo es blanco puro, no un gris claro.
   *
   * Antes el fondo era `neutral[50]` y las tarjetas blancas encima: la
   * jerarquía la hacía un escalón de gris. Ahora la hacen el borde de un
   * píxel y una sombra mínima, y el gris se reserva para las zonas que deben
   * hundirse — barra lateral, cabecera de tabla, wells. Es lo que hace que
   * una herramienta de datos se lea despejada en vez de apagada.
   */
  canvas: neutral[0],
  surface: neutral[0],
  surfaceRaised: neutral[0],
  surfaceSunken: neutral[50],
  surfaceHover: neutral[100],
  surfaceActive: neutral[200],
  surfaceHeader: neutral[50],
  // Velo más ligero: sobre un fondo blanco, un 55 % de negro resulta brutal.
  overlay: 'rgba(13, 14, 17, 0.45)',

  border: neutral[200],
  borderStrong: neutral[300],
  borderSubtle: neutral[100],
  // Paso intermedio entre neutral[400] (2.62:1, insuficiente) y neutral[500]
  // (4.81:1, visualmente pesado para el contorno de un campo).
  borderInput: '#8B8F99',
  borderFocus: green[500],

  fg: neutral[900],
  fgMuted: neutral[600],
  fgSubtle: neutral[500],
  fgDisabled: neutral[400],
  fgOnAccent: neutral[0],
  link: green[700],
  linkHover: green[800],

  primary: green[600],
  primaryHover: green[700],
  primaryActive: green[800],
  primaryFg: neutral[0],
  primarySubtle: green[50],
  primarySubtleFg: green[800],
  primaryBorder: green[200],

  accent: amber[500],
  accentHover: amber[600],
  accentFg: amber[950],
  accentSubtle: amber[50],
  accentSubtleFg: amber[800],
  accentBorder: amber[200],

  success: green[600],
  successFg: neutral[0],
  successSubtle: green[50],
  successSubtleFg: green[800],
  successBorder: green[200],

  warning: amber[500],
  warningFg: amber[950],
  warningSubtle: amber[50],
  warningSubtleFg: amber[800],
  warningBorder: amber[200],

  danger: red[600],
  dangerHover: red[700],
  dangerFg: neutral[0],
  dangerSubtle: red[50],
  dangerSubtleFg: red[800],
  dangerBorder: red[200],

  info: blue[600],
  infoFg: neutral[0],
  infoSubtle: blue[50],
  infoSubtleFg: blue[800],
  infoBorder: blue[200],

  focusRing: green[500],
  track: neutral[200],
  divider: neutral[200],
  skeleton: neutral[100],
  highlight: amber[200],
  highlightFg: amber[900],
};

export const darkColors: SemanticColors = {
  canvas: neutral[950],
  surface: neutral[900],
  surfaceRaised: '#1E2025',
  surfaceSunken: '#08090B',
  surfaceHover: '#202227',
  surfaceActive: '#2A2C33',
  surfaceHeader: '#131418',
  overlay: 'rgba(4, 5, 7, 0.72)',

  border: neutral[800],
  borderStrong: neutral[700],
  borderSubtle: '#1F2126',
  borderInput: neutral[500],
  borderFocus: green[400],

  fg: neutral[100],
  fgMuted: neutral[400],
  fgSubtle: neutral[500],
  fgDisabled: neutral[600],
  fgOnAccent: green[950],
  link: green[300],
  linkHover: green[200],

  primary: green[400],
  primaryHover: green[300],
  primaryActive: green[200],
  primaryFg: green[950],
  primarySubtle: 'rgba(63, 184, 124, 0.14)',
  primarySubtleFg: green[300],
  primaryBorder: 'rgba(63, 184, 124, 0.32)',

  accent: amber[400],
  accentHover: amber[300],
  accentFg: amber[950],
  accentSubtle: 'rgba(245, 173, 35, 0.14)',
  accentSubtleFg: amber[300],
  accentBorder: 'rgba(245, 173, 35, 0.32)',

  success: green[400],
  successFg: green[950],
  successSubtle: 'rgba(63, 184, 124, 0.14)',
  successSubtleFg: green[300],
  successBorder: 'rgba(63, 184, 124, 0.32)',

  warning: amber[400],
  warningFg: amber[950],
  warningSubtle: 'rgba(245, 173, 35, 0.14)',
  warningSubtleFg: amber[300],
  warningBorder: 'rgba(245, 173, 35, 0.32)',

  danger: red[400],
  dangerHover: red[300],
  dangerFg: red[950],
  dangerSubtle: 'rgba(239, 68, 68, 0.16)',
  dangerSubtleFg: red[300],
  dangerBorder: 'rgba(239, 68, 68, 0.34)',

  info: blue[400],
  infoFg: blue[950],
  infoSubtle: 'rgba(96, 165, 250, 0.16)',
  infoSubtleFg: blue[300],
  infoBorder: 'rgba(96, 165, 250, 0.34)',

  focusRing: green[400],
  track: neutral[800],
  divider: neutral[800],
  skeleton: '#212328',
  highlight: 'rgba(245, 173, 35, 0.28)',
  highlightFg: amber[200],
};

/**
 * Paleta categórica para gráficos. Ocho series con tonos bien separados y
 * luminosidad perceptual pareja, para que ninguna serie "grite" más que otra.
 * Se define por tema porque en oscuro hay que subir la luminosidad.
 */
export const chartCategorical = {
  light: [
    green[500],
    blue[600],
    amber[600],
    violet[600],
    teal[600],
    red[600],
    pink[600],
    neutral[500],
  ],
  dark: [
    green[400],
    blue[400],
    amber[300],
    violet[400],
    teal[400],
    red[400],
    pink[400],
    neutral[400],
  ],
} as const;

/** Rampa secuencial (una sola métrica, de menos a más). */
export const chartSequential = {
  light: [green[100], green[200], green[300], green[400], green[500], green[600], green[700]],
  dark: [green[900], green[800], green[700], green[600], green[500], green[400], green[300]],
} as const;

/**
 * Rampa divergente para "antes vs después" de la implementación del CRM:
 * rojo = empeoró, neutro = sin cambio, verde = mejoró.
 */
export const chartDiverging = {
  light: [red[600], red[400], red[200], neutral[200], green[200], green[400], green[600]],
  dark: [red[400], red[300], 'rgba(239,68,68,0.35)', neutral[700], 'rgba(63,184,124,0.35)', green[300], green[400]],
} as const;

/** Colores de soporte de gráficos (ejes, rejilla, referencia de meta). */
export interface ChartSupport {
  grid: string;
  axis: string;
  axisLabel: string;
  tooltipBg: string;
  tooltipFg: string;
  /** Línea punteada que marca la meta del indicador. */
  target: string;
  /** Área sombreada del periodo de comparación (pre-implementación). */
  comparison: string;
}

export const chartSupport: Record<ColorScheme, ChartSupport> = {
  light: {
    grid: neutral[200],
    axis: neutral[300],
    axisLabel: neutral[500],
    tooltipBg: neutral[900],
    tooltipFg: neutral[50],
    target: neutral[500],
    comparison: 'rgba(110, 114, 124, 0.16)',
  },
  dark: {
    grid: '#23252B',
    axis: neutral[700],
    axisLabel: neutral[400],
    tooltipBg: '#2A2C33',
    tooltipFg: neutral[50],
    target: neutral[400],
    comparison: 'rgba(156, 160, 168, 0.16)',
  },
} as const;

export const colorSchemes: Record<ColorScheme, SemanticColors> = {
  light: lightColors,
  dark: darkColors,
};
